import React from 'react'
import { Loading, Text } from '@workwhile/ui'
import {
  Calendar,
  Car,
  CheckCircle2,
  Clock,
  FileText,
  Hash,
  Mail,
  Phone,
  Shield,
  Star,
  User as UserIcon,
} from 'lucide-react'
import {
  CommandPaletteOption,
  CopyableValue,
  DetailsDescription,
  DetailsTitle,
  formatDate,
  getValue,
  SectionTitleWithButton,
  UserEntry,
} from '../CommandPalette_Utils'
import { useSearchUsers } from '../../../queries/user/useSearchUsers'
import { InfoTable } from '../../../components/InfoTable/InfoTable'

type UserDetailsProps = {
  result: UserEntry
  onNavigateToUser: (userId: string | number) => void
  onItemInteraction: (item: CommandPaletteOption) => void
}

export function UserDetails({
  result,
  onNavigateToUser,
  onItemInteraction,
}: UserDetailsProps) {
  const userIdString = String(result.id)
  const { data: userSearchResults, isLoading: isLoadingUserDetails } =
    useSearchUsers({
      keyword: userIdString,
      staleTime: 1000 * 60 * 5, // 5 minutes
    })
  const detailedUser = userSearchResults?.find(
    (user) => String(user.id) === userIdString
  )

  const handleSearch = () => {
    const userId = result.id
    if (userId && (typeof userId === 'string' || typeof userId === 'number')) {
      onItemInteraction(result)
      onNavigateToUser(userId)
    }
  }
  return (
    <>
      <DetailsTitle>
        {result.name || 'N/A'}
        {isLoadingUserDetails ? (
          <span style={{ marginLeft: '8px' }}>
            <Loading type="button" />
          </span>
        ) : null}
      </DetailsTitle>
      <DetailsDescription>
        <div style={{ marginBottom: '16px' }}>
          <SectionTitleWithButton
            title="Basic Information"
            buttonText="Go To User"
            onButtonClick={handleSearch}
            buttonTitle="Search for this user"
          />
          <InfoTable>
            <InfoTable.Row
              icon={<Hash size={14} />}
              label="User ID"
              value={
                <CopyableValue
                  value={result.id}
                  fieldName="User ID"
                  item={result}
                  onItemInteraction={onItemInteraction}
                />
              }
            />
            <InfoTable.Row
              icon={<UserIcon size={14} />}
              label="Name"
              value={
                <CopyableValue
                  value={result.name ?? 'N/A'}
                  fieldName="Name"
                  item={result}
                  onItemInteraction={onItemInteraction}
                />
              }
            />
            <InfoTable.Row
              icon={<Mail size={14} />}
              label="Email"
              value={
                <CopyableValue
                  value={result.email ?? 'N/A'}
                  fieldName="Email"
                  item={result}
                  onItemInteraction={onItemInteraction}
                />
              }
            />
            <InfoTable.Row
              icon={<Phone size={14} />}
              label="Phone"
              value={
                <CopyableValue
                  value={detailedUser?.phoneNumber ?? 'N/A'}
                  fieldName="Phone"
                  item={result}
                  onItemInteraction={onItemInteraction}
                />
              }
            />
            <InfoTable.Row
              icon={<Hash size={14} />}
              label="Firebase ID"
              value={
                <CopyableValue
                  value={detailedUser?.firebaseId ?? 'N/A'}
                  fieldName="Firebase ID"
                  item={result}
                  onItemInteraction={onItemInteraction}
                />
              }
            />
          </InfoTable>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <Text
            fontSize={2}
            fontWeight={500}
            color="text"
            style={{ marginBottom: '8px' }}
          >
            Status & Verification
          </Text>
          <InfoTable>
            <InfoTable.Row
              icon={<CheckCircle2 size={14} />}
              label="Status"
              value={detailedUser?.status ?? 'N/A'}
            />
            <InfoTable.Row
              icon={<FileText size={14} />}
              label="Orientation"
              value={detailedUser?.orientationStatus ?? 'N/A'}
            />
            <InfoTable.Row
              icon={<FileText size={14} />}
              label="W2 Status"
              value={detailedUser?.w2Status ?? 'N/A'}
            />
            <InfoTable.Row
              icon={<Star size={14} />}
              label="Rating"
              value={
                detailedUser?.rating ? detailedUser.rating.toFixed(2) : 'N/A'
              }
            />
            <InfoTable.Row
              icon={<Shield size={14} />}
              label="Shadow Blocked"
              value={getValue(detailedUser?.shadowBlocked)}
            />
            <InfoTable.Row
              icon={<FileText size={14} />}
              label="Has SSN"
              value={getValue(detailedUser?.hasSsn)}
            />
            <InfoTable.Row
              icon={<Star size={14} />}
              label="Tier Experienced"
              value={getValue(detailedUser?.tierExperienced)}
            />
          </InfoTable>
        </div>

        <div>
          <Text
            fontSize={2}
            fontWeight={500}
            color="text"
            style={{ marginBottom: '8px' }}
          >
            Work & Background
          </Text>
          <InfoTable>
            <InfoTable.Row
              icon={<Calendar size={14} />}
              label="Created"
              value={
                detailedUser?.createdAt
                  ? formatDate(detailedUser.createdAt)
                  : 'N/A'
              }
            />
            <InfoTable.Row
              icon={<Clock size={14} />}
              label="Hours Target"
              value={detailedUser?.workProfile?.numHoursDesired ?? 'N/A'}
            />
            <InfoTable.Row
              icon={<FileText size={14} />}
              label="Wants Full Time"
              value={getValue(detailedUser?.workProfile?.wantsFullTime)}
            />
            <InfoTable.Row
              icon={<FileText size={14} />}
              label="Currently Employed"
              value={getValue(detailedUser?.workProfile?.currentlyEmployed)}
            />
            <InfoTable.Row
              icon={<FileText size={14} />}
              label="Has License"
              value={getValue(detailedUser?.workProfile?.hasLicense)}
            />
            <InfoTable.Row
              icon={<Car size={14} />}
              label="Has Car"
              value={getValue(detailedUser?.workProfile?.hasCar)}
            />
          </InfoTable>
        </div>
      </DetailsDescription>
    </>
  )
}
